import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import React from 'react'
import { fetchRmReco } from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'

// R&M reco (Satya, 4 Nov 2026), laid out like the "Ali - Petty cash for
// Repair & Maintenance" sheet: per centre, what Finance paid (the payment
// file), what Azam paid, what the centre team spent, their total, everything
// in Alfred, the R&M budget and budget minus Alfred. Total and Alfred differ
// only by lines with no Paid by (older lines), shown as "not marked".

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), width: '100%', boxSizing: 'border-box' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  table: {
    '& th': { fontWeight: 600, whiteSpace: 'normal' },
    '& td': { whiteSpace: 'normal' },
    '& td, & th': { borderRight: '1px solid #eee' }
  },
  totalRow: { '& td': { fontWeight: 700, background: theme.palette.grey[50] } }
}))

const num = (v) =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN')

const COLS = [
  { key: 'finance', label: 'finance (a)' },
  { key: 'azam', label: "azam (b)" },
  { key: 'centre_team', label: 'centre team (c)' },
  { key: 'total', label: 'total (a+b+c)' },
  { key: 'alfred', label: 'alfred' },
  { key: 'budget', label: 'budget' },
  { key: 'balance', label: 'budget − alfred' }
]

const SEARCH_COLS = COLS.reduce(
  (acc, c) => ({ ...acc, [c.key]: (r) => r[c.key] }),
  { centre: (r) => r.centre_name }
)

const HqRmReco = ({ initialMonth }) => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(
    initialMonth || Dayjs().format('YYYY-MM')
  )
  const [allRows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    let live = true
    setLoading(true)
    fetchRmReco(month)
      .then((d) => live && setRows(d?.results || []))
      .catch((error) => {
        if (!live) return
        setRows([])
        eventAlert(
          error?.message || 'Failed to load the reco',
          constants.ERROR,
          'R&M reco'
        )
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [month])

  const { rows, box } = useColumnSearch(allRows, SEARCH_COLS)
  const sum = (key) =>
    rows.reduce(
      (s, r) => (r[key] === null || r[key] === undefined ? s : s + Number(r[key])),
      0
    )
  const unmarked = sum('unmarked')

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.toolbar}>
        <Typography variant="h6">r&m reco</Typography>
        <TextField
          type="month"
          label="month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </div>
      {unmarked > 0 && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#fff8e1', borderRadius: 8, color: '#8d6e00' }}>
          ₹{unmarked.toLocaleString('en-IN')} of r&m in alfred has no paid by
          (filed before it existed), so total and alfred differ by that much.
        </Box>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box width="100%">
          <Table
            size="small"
            className={classes.table}
            style={{ width: '100%', margin: 0, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <SearchHeadCell
                  label="centre"
                  search={box('centre')}
                  style={{ width: 150 }}
                />
                {COLS.map((c) => (
                  <SearchHeadCell
                    key={c.key}
                    label={c.label}
                    align="right"
                    search={box(c.key)}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.centre} hover>
                  <TableCell>{r.centre_name}</TableCell>
                  {COLS.map((c) => (
                    <TableCell
                      key={c.key}
                      align="right"
                      style={
                        c.key === 'balance' && Number(r.balance) < 0
                          ? { background: '#f4c7c3' }
                          : undefined
                      }>
                      {num(r[c.key])}
                      {c.key === 'alfred' && Number(r.unmarked) > 0 && (
                        <Typography
                          variant="caption"
                          display="block"
                          style={{ color: '#8d6e00' }}>
                          incl. {num(r.unmarked)} not marked
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {rows.length > 0 && (
                <TableRow className={classes.totalRow}>
                  <TableCell>total</TableCell>
                  {COLS.map((c) => (
                    <TableCell key={c.key} align="right">
                      {num(sum(c.key))}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  )
}

export default HqRmReco
